import { Fragment, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, SlidersHorizontal, PlusCircle, AlertCircle, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { LoadingState, ErrorState } from '@/components/ui/loading-state'
import { useAccount } from '@/contexts/AccountContext'
import { useConsumableBalances, useLedgerHistory } from '../queries/consumableBalances'
import { AdjustBalanceModal } from '../components/AdjustBalanceModal'
import { LoadCreditsModal } from '../components/LoadCreditsModal'
import type { ConsumableBalance } from '../types/consumableBalance.types'

const EVENT_TYPE_BADGE: Record<string, 'success' | 'warning' | 'info' | 'secondary' | 'destructive'> = {
  LOAD: 'success',
  RESERVE: 'info',
  CONSUME: 'secondary',
  RELEASE: 'info',
  EXPIRE: 'warning',
  ADJUSTMENT: 'warning',
}

function formatDate(iso?: string): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function totalOrDash(v: number | null | undefined): string {
  return v === null || v === undefined ? '—' : v.toLocaleString()
}

/** Shown above the ledger when a row expands — null values mean this pool has never had a Load
 *  Credits action, so there's nothing to reconcile yet. */
function LoadTotalsSummary({ balance }: { balance: ConsumableBalance }) {
  const hasAnyTotals =
    balance.totalCreditsLoadedPromo != null ||
    balance.totalCreditsLoadedTransactional != null ||
    balance.totalConsumedPromo != null ||
    balance.totalConsumedTransactional != null

  if (!hasAnyTotals) {
    return (
      <div className="px-4 py-3 bg-gray-50/50 border-b border-gray-100 text-xs text-gray-500">
        No Load Credits action recorded yet for {balance.channel} — total loaded / consumed will appear here once
        one is.
      </div>
    )
  }

  return (
    <div className="px-4 py-3 bg-gray-50/50 border-b border-gray-100">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <div>
          <div className="text-gray-500">Total Loaded (Promo)</div>
          <div className="font-mono text-gray-900">{totalOrDash(balance.totalCreditsLoadedPromo)}</div>
        </div>
        <div>
          <div className="text-gray-500">Total Loaded (Transactional)</div>
          <div className="font-mono text-gray-900">{totalOrDash(balance.totalCreditsLoadedTransactional)}</div>
        </div>
        <div>
          <div className="text-gray-500">Total Consumed (Promo)</div>
          <div className="font-mono text-gray-900">{totalOrDash(balance.totalConsumedPromo)}</div>
        </div>
        <div>
          <div className="text-gray-500">Total Consumed (Transactional)</div>
          <div className="font-mono text-gray-900">{totalOrDash(balance.totalConsumedTransactional)}</div>
        </div>
      </div>
      <p className="text-[11px] text-gray-400 mt-2">
        Total consumed is ground truth from campaign_events as of the last Load Credits action — not live; it only
        refreshes on the next load.
      </p>
    </div>
  )
}

function LedgerHistory({ accountId, channel }: { accountId: string; channel: string }) {
  const { data, isLoading, isError, error, refetch } = useLedgerHistory(accountId, channel)

  if (isLoading) {
    return (
      <div className="p-6">
        <LoadingState message="Loading ledger history…" />
      </div>
    )
  }
  if (isError) {
    return (
      <div className="p-6">
        <ErrorState message={(error as Error).message} onRetry={refetch} />
      </div>
    )
  }
  const entries = data?.content ?? []
  if (entries.length === 0) {
    return <div className="p-6 text-center text-gray-500 text-sm">No ledger entries yet for this channel.</div>
  }

  return (
    <div className="overflow-x-auto bg-gray-50/50">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left px-4 py-2 font-medium text-gray-600 whitespace-nowrap">Date</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600 whitespace-nowrap">Event</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600 whitespace-nowrap">Pool</th>
            <th className="text-right px-4 py-2 font-medium text-gray-600 whitespace-nowrap">Quantity</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Reason / Reference</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600 whitespace-nowrap">By</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id} className="border-b border-gray-100">
              <td className="px-4 py-2 text-gray-500 whitespace-nowrap">{formatDate(entry.createdDate)}</td>
              <td className="px-4 py-2 whitespace-nowrap">
                <Badge variant={EVENT_TYPE_BADGE[entry.eventType] ?? 'secondary'}>{entry.eventType}</Badge>
              </td>
              <td className="px-4 py-2 text-gray-700 capitalize whitespace-nowrap">{entry.messageType}</td>
              <td className="px-4 py-2 text-right font-mono whitespace-nowrap">
                {entry.eventType === 'ADJUSTMENT' ? '±' : ''}
                {entry.quantity.toLocaleString()}
              </td>
              <td className="px-4 py-2 text-gray-600 max-w-sm truncate" title={entry.referenceId}>
                {entry.referenceId || '—'}
              </td>
              <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{entry.createdBy}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function ConsumableBalancesPage() {
  const { selectedAccountId } = useAccount()
  const [adjustTarget, setAdjustTarget] = useState<{ channel: string } | null>(null)
  const [loadTarget, setLoadTarget] = useState<{ channel: string } | null>(null)
  const [expandedChannel, setExpandedChannel] = useState<string | null>(null)

  const accountId = selectedAccountId && selectedAccountId !== 'ALL' ? selectedAccountId : null

  const { data: balances = [], isLoading, isError, error, refetch, isFetching } = useConsumableBalances(accountId)

  const pageHeader = (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Link to="/finance" className="text-gray-400 hover:text-gray-600 transition-colors">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Consumable Balances</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            View and manually correct per-channel credit balances — every adjustment is applied and audited
            immediately, with no separate approval step.
          </p>
        </div>
      </div>
      {accountId && (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 mr-1.5 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => setLoadTarget({ channel: '' })}>
            <PlusCircle className="h-4 w-4 mr-1.5" />
            Load Credits
          </Button>
          <Button size="sm" onClick={() => setAdjustTarget({ channel: '' })}>
            <SlidersHorizontal className="h-4 w-4 mr-1.5" />
            Adjust Balance
          </Button>
        </div>
      )}
    </div>
  )

  if (!accountId) {
    return (
      <div className="p-6 space-y-6">
        {pageHeader}
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 mx-auto mb-4">
            <AlertCircle className="h-7 w-7 text-amber-600" />
          </div>
          <p className="text-gray-600">Select an account to view or adjust consumable balances.</p>
        </div>
      </div>
    )
  }

  const toggleExpanded = (channel: string) => {
    setExpandedChannel((current) => (current === channel ? null : channel))
  }

  return (
    <div className="p-6 space-y-6">
      {pageHeader}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Balances — Account {accountId}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8">
              <LoadingState message="Loading consumable balances…" />
            </div>
          ) : isError ? (
            <div className="p-8">
              <ErrorState message={(error as Error).message} onRetry={refetch} />
            </div>
          ) : balances.length === 0 ? (
            <div className="p-12 text-center text-gray-500 text-sm">
              No consumable balance rows yet for this account. Use "Adjust Balance" to create one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="w-8 px-4 py-3"></th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Channel</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Promo Available</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Transactional Available</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Reserved Promo</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Reserved Transactional</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Last Updated</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600"></th>
                  </tr>
                </thead>
                <tbody>
                  {balances.map((bal: ConsumableBalance) => (
                    <Fragment key={bal.channel}>
                      <tr
                        className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => toggleExpanded(bal.channel)}
                      >
                        <td className="px-4 py-3 text-gray-400">
                          {expandedChannel === bal.channel ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{bal.channel}</td>
                        <td className="px-4 py-3 text-right font-mono whitespace-nowrap">{bal.promoAvailable.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right font-mono whitespace-nowrap">{bal.transactionalAvailable.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right font-mono text-gray-500 whitespace-nowrap">{bal.reservedPromo.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right font-mono text-gray-500 whitespace-nowrap">{bal.reservedTransactional.toLocaleString()}</td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                          {formatDate(bal.updatedDate)}
                          {bal.updatedBy && <span className="text-gray-400"> · {bal.updatedBy}</span>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex gap-1.5">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs gap-1"
                              onClick={(e) => {
                                e.stopPropagation()
                                setLoadTarget({ channel: bal.channel })
                              }}
                            >
                              <PlusCircle className="h-3 w-3" /> Load
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs gap-1"
                              onClick={(e) => {
                                e.stopPropagation()
                                setAdjustTarget({ channel: bal.channel })
                              }}
                            >
                              <SlidersHorizontal className="h-3 w-3" /> Adjust
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {expandedChannel === bal.channel && (
                        <tr>
                          <td colSpan={8} className="p-0 border-b border-gray-200">
                            <LoadTotalsSummary balance={bal} />
                            <LedgerHistory accountId={accountId} channel={bal.channel} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {adjustTarget && (
        <AdjustBalanceModal
          accountId={accountId}
          defaultChannel={adjustTarget.channel || undefined}
          onClose={() => setAdjustTarget(null)}
        />
      )}

      {loadTarget && (
        <LoadCreditsModal
          accountId={accountId}
          defaultChannel={loadTarget.channel || undefined}
          onClose={() => setLoadTarget(null)}
        />
      )}
    </div>
  )
}
